/**
 * `/permissions` — the catalogue, read-only.
 *
 * Nothing on this screen changes anything, and there is no endpoint that could:
 * **permissions are defined in code**, so the set is fixed for a given build.
 * The screen exists because "what can this system actually do, and which roles
 * currently do it" is a question people ask constantly while setting up access,
 * and answering it otherwise means opening thirty role editors in turn.
 *
 * So each permission is listed with the roles that hold it — the reverse of the
 * role editor's view, computed here from the role list rather than from an
 * endpoint, because there is no endpoint for it and the data is already loaded.
 */
import { Link as RouterLink } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { RoleDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { PageHeader } from '../../components/PageHeader';
import { useAsync } from '../../hooks/useAsync';
import { listPermissions, listRoles } from '../../api/identity';
import { humanise, splitPermission } from '../../utils/format';

/**
 * The three the routes README singles out, as **keys** into the `people`
 * namespace. Permission names are identifiers from the backend and stay as they
 * are; only the prose beside them is translated.
 */
const NOTE_KEYS = {
  'notification.manage': 'permissions.note.notificationManage',
  'audit.read': 'permissions.note.auditRead',
  'resource.share': 'permissions.note.resourceShare',
} as const;

type NoteKey = (typeof NOTE_KEYS)[keyof typeof NOTE_KEYS];

/** The note for a permission, or nothing. Typed so `t()` stays checked. */
function noteKeyFor(permission: string): NoteKey | undefined {
  return (NOTE_KEYS as Record<string, NoteKey | undefined>)[permission];
}

export default function PermissionsPage() {
  const { t } = useTranslation('people');
  const catalogue = useAsync(() => listPermissions(), []);
  // Every role, not a page: this screen answers "who holds this permission?",
  // and a paged answer would quietly omit holders. 500 is the endpoint's
  // maximum; a deployment with more roles than that has a different problem.
  const roles = useAsync(() => listRoles({ limit: 500 }), []);

  function holdersOf(permission: string): RoleDto[] {
    return (roles.data?.roles ?? []).filter((role) => role.permissions.includes(permission));
  }

  return (
    <>
      <PageHeader
        title={t('permissions.title')}
        description={t('permissions.description')}
      />

      <Alert severity="info" icon={<ShieldOutlinedIcon />} sx={{ mb: 3 }}>
        {/* Trans, not t: the emphasis sits mid-sentence, and where it falls is
            part of the translation rather than of this markup. */}
        <Trans i18nKey="permissions.twoChecks" ns="people" components={{ em: <em /> }} />
      </Alert>

      <AsyncContent state={catalogue}>
        {({ byDomain }) => (
          <Stack spacing={3}>
            {Object.entries(byDomain).map(([domain, permissions]) => (
              <Box key={domain}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: 700, textTransform: 'capitalize' }}
                >
                  {domain}
                </Typography>
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '30%' }}>
                          {t('permissions.column.permission')}
                        </TableCell>
                        <TableCell sx={{ width: '25%' }}>
                          {t('permissions.column.allows')}
                        </TableCell>
                        <TableCell>{t('permissions.column.heldBy')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {permissions.map((permission) => {
                        const { verb } = splitPermission(permission);
                        const noteKey = noteKeyFor(permission);
                        const holders = holdersOf(permission);

                        return (
                          <TableRow key={permission} hover>
                            <TableCell>
                              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {permission}
                                </Typography>
                                {noteKey && (
                                  <Tooltip title={t(noteKey)}>
                                    <WarningAmberIcon
                                      fontSize="inherit"
                                      color="warning"
                                      sx={{ fontSize: 16 }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {humanise(verb)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {holders.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                  {t('permissions.noHolders')}
                                </Typography>
                              ) : (
                                <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                  {holders.map((role) => (
                                    <Chip
                                      key={role.id}
                                      size="small"
                                      variant="outlined"
                                      label={role.name}
                                      component={RouterLink}
                                      to={`/roles/${role.id}`}
                                      clickable
                                    />
                                  ))}
                                </Stack>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </Box>
            ))}
          </Stack>
        )}
      </AsyncContent>
    </>
  );
}
